import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Instagram, Facebook } from "lucide-react";
import InquiryForm from "@/components/InquiryForm";

const contactInfo = [
  {
    icon: Mail,
    title: "Email Us",
    details: "info@luxurybeauty.ca",
    link: "mailto:info@luxurybeauty.ca",
  },
  {
    icon: Phone,
    title: "Call Us",
    details: "+592 647 1133",
    link: "tel:+5926471133",
  },
  {
    icon: MapPin,
    title: "Visit Us",
    details: "Lot 18 Helena #1, Mahaica, East Coast Demerara, Guyana",
    link: null,
  },
  {
    icon: Clock,
    title: "By Appointment",
    details: "Saturdays & Weekdays",
    link: null,
  },
];

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const ContactPage = () => {
  const [inquiryPurpose, setInquiryPurpose] = useState("service");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Contact Us
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              Get In<br />
              <span className="gold-gradient">Touch</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] leading-relaxed">
              Whether you're looking for a luxurious treatment or want to learn 
              professional beauty skills, we're here to help. Book an appointment 
              or register for our training courses.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-[#0A0A0A]" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <AnimatedSection key={info.title}>
                <div
                  className="bento-card p-6 text-center h-full"
                  data-testid={`contact-card-${index}`}
                >
                  <div className="w-14 h-14 mx-auto mb-4 border border-[#C5A059] flex items-center justify-center">
                    <info.icon className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <h3 className="font-heading text-lg text-white mb-2">{info.title}</h3>
                  {info.link ? (
                    <a
                      href={info.link}
                      className="font-body text-[#A3A3A3] hover:text-[#C5A059] transition-colors duration-300"
                    >
                      {info.details}
                    </a>
                  ) : (
                    <p className="font-body text-[#A3A3A3]">{info.details}</p>
                  )}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="contact-main">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Info */}
            <AnimatedSection>
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Services & Training
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-light text-white mt-4 mb-6">
                  How Can We Help?
                </h2>
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-8">
                  We offer both luxury beauty services and professional training courses. 
                  Select below whether you want to book an appointment or register for a course.
                </p>

                {/* Purpose Toggle */}
                <div className="flex gap-4 mb-8">
                  <button
                    onClick={() => setInquiryPurpose("service")}
                    data-testid="toggle-service"
                    className={`flex-1 py-4 font-body text-sm tracking-wider uppercase transition-all duration-300 ${
                      inquiryPurpose === "service"
                        ? "bg-[#C5A059] text-[#050505]"
                        : "border border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]"
                    }`}
                  >
                    Book Service
                  </button>
                  <button
                    onClick={() => setInquiryPurpose("training")}
                    data-testid="toggle-training"
                    className={`flex-1 py-4 font-body text-sm tracking-wider uppercase transition-all duration-300 ${
                      inquiryPurpose === "training"
                        ? "bg-[#C5A059] text-[#050505]"
                        : "border border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]"
                    }`}
                  >
                    Course Training
                  </button>
                </div>

                {/* Info based on purpose */}
                {inquiryPurpose === "service" ? (
                  <div className="bento-card p-6 mb-8">
                    <h4 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                      Our Services
                    </h4>
                    <ul className="space-y-2 text-[#A3A3A3] text-sm">
                      <li>• Facials (Basic, LED, High Frequency, Chemical)</li>
                      <li>• Nails (Manicure, Pedicure, GelX, Acrylic)</li>
                      <li>• Lash Extensions</li>
                      <li>• Complete Body Waxing</li>
                      <li>• Hair Services (Color, Keratin, Silk Press)</li>
                      <li>• Wig Install, Coloring & Revamping</li>
                    </ul>
                    <p className="mt-4 text-[#C5A059] text-xs tracking-wider uppercase">
                      By Appointment Only
                    </p>
                  </div>
                ) : (
                  <div className="bento-card p-6 mb-8">
                    <h4 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                      Training Courses (GYD)
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A3A3A3]">Facial Course</span>
                        <span className="text-white">$60,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A3A3A3]">Manicure & Pedicure</span>
                        <span className="text-white">$45,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A3A3A3]">Acrylic Nails</span>
                        <span className="text-white">$50,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A3A3A3]">Lash Extensions</span>
                        <span className="text-white">$40,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A3A3A3]">Lash Lift</span>
                        <span className="text-white">$45,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A3A3A3]">Body Waxing</span>
                        <span className="text-white">$60,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A3A3A3]">Hair Services</span>
                        <span className="text-white">$55,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A3A3A3]">Advanced Hair</span>
                        <span className="text-white">$60,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A3A3A3]">Brow Training</span>
                        <span className="text-white">$40,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A3A3A3]">Wig Master Class</span>
                        <span className="text-white">$80,000</span>
                      </div>
                    </div>
                    <p className="mt-4 text-[#C5A059] text-xs tracking-wider uppercase">
                      Kit fees additional • Limited Spaces
                    </p>
                  </div>
                )}

                {/* Social Links */}
                <div>
                  <h4 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#A3A3A3] mb-4">
                    Follow Us
                  </h4>
                  <div className="flex gap-4">
                    <a
                      href="https://instagram.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-social-instagram"
                      className="w-12 h-12 border border-[#262626] flex items-center justify-center text-[#A3A3A3] hover:border-[#C5A059] hover:text-[#C5A059] transition-all duration-300"
                    >
                      <Instagram size={20} />
                    </a>
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-social-facebook"
                      className="w-12 h-12 border border-[#262626] flex items-center justify-center text-[#A3A3A3] hover:border-[#C5A059] hover:text-[#C5A059] transition-all duration-300"
                    >
                      <Facebook size={20} />
                    </a>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Right Column - Form */}
            <AnimatedSection>
              <InquiryForm inquiryType={inquiryPurpose} />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-[#0A0A0A]" data-testid="map-section">
        <div className="h-[400px] relative">
          <div className="absolute inset-0 bg-[#121212] flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
              <h3 className="font-heading text-2xl text-white mb-2">Our Location</h3>
              <p className="font-body text-[#A3A3A3]">
                Lot 18 Helena #1<br />
                Mahaica, East Coast Demerara<br />
                Guyana
              </p>
              <p className="font-body text-sm text-[#C5A059] mt-4">
                The only salon/spa of its kind in Georgetown & Mahaica
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
